<template>
    <div class="product-v3-container">
        <ProductStatsNavigation
            :stats="stats"
            :activeTab="activeTab"
            :can-consult-products="canConsultProducts"
            :can-consult-categories="canConsultCategories"
            :can-consult-suppliers="canConsultSuppliers"
            :can-consult-brands="canConsultBrands"
            @click="setActiveTab"
        />
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Tab Title / Breadcrumb -->
            <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                <template v-if="activeTab === 'categories' && categoryBreadcrumb">
                    <button
                        @click="clearCategoryBreadcrumb"
                        class="poppinsRegular ppv2-text-black hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
                        :style="{ fontSize: normalizeFontSize(26) }"
                    >
                        {{ $t('productCategories.title2') || 'Catégorie' }}
                    </button>
                    <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(26) }">></span>
                    <h2 class="poppinsRegular ppv2-text-black text-capitalize my-0" :style="{ fontSize: normalizeFontSize(26) }">
                        {{ categoryBreadcrumb }}
                    </h2>
                </template>
                <template v-else>
                    <Header :title="tabTitle" />
                </template>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                ></div>
            </div>

            <!-- Tab Content -->
            <component
                v-else
                :is="currentTabComponent"
                :key="activeTab"
                :user-id="userId"
                ref="tabComponent"
                @refresh-stats="fetchStats"
                @breadcrumb-change="handleBreadcrumbChange"
                :token="token"
            />
        </div>
    </div>
</template>

<script>
import ProductsTable from './ProductsTable.vue'
import CategorieTable from './Categorie/CategorieTable.vue'
import SupplierTable from './Supplier/SupplierTable.vue'
import BrandTable from './Brand/BrandTable.vue'
import ProductStatsNavigation from './ProductStatsNavigation.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'

export default {
    name: 'ProductV3Index',
    components: {
        ProductsTable,
        CategorieTable,
        SupplierTable,
        BrandTable,
        ProductStatsNavigation,
        Header,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        initialTab: {
            type: String,
            default: 'products',
        },
        token: {
            type: String,
            default: '',
        },
        canConsultProducts: {
            type: [Boolean, String],
            default: true,
        },
        canConsultCategories: {
            type: [Boolean, String],
            default: true,
        },
        canConsultSuppliers: {
            type: [Boolean, String],
            default: true,
        },
        canConsultBrands: {
            type: [Boolean, String],
            default: true,
        },
    },
    data() {
        return {
            activeTab: this.initialTab,
            isLoading: true,
            stats: {
                products: 0,
                categories: 0,
                suppliers: 0,
                brands: 0,
            },
            categoryBreadcrumb: null,
        }
    },
    computed: {
        currentTabComponent() {
            const components = {
                products: 'ProductsTable',
                categories: 'CategorieTable',
                suppliers: 'SupplierTable',
                brands: 'BrandTable',
            }
            return components[this.activeTab] || 'ProductsTable'
        },
        tabTitle() {
            const titles = {
                products: this.$t('product.title') || 'Produits',
                categories: this.$t('productCategories.title') || 'Catégories',
                suppliers: this.$t('productSupplier.title') || 'Fournisseurs',
                brands: this.$t('productBrand.title') || 'Marques',
            }
            return titles[this.activeTab] || 'Produits'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        setActiveTab(tab) {
            this.activeTab = tab
            // Clear breadcrumb when switching tabs
            this.categoryBreadcrumb = null

            // Update URL query parameter without reloading
            const url = new URL(window.location.href)
            url.searchParams.set('tab', tab)
            window.history.pushState({}, '', url.toString())
        },
        handleBreadcrumbChange(categoryName) {
            this.categoryBreadcrumb = categoryName
        },
        clearCategoryBreadcrumb() {
            this.categoryBreadcrumb = null
            // Tell the CategorieTable to reset
            if (this.$refs.tabComponent && this.$refs.tabComponent.resetDrillDown) {
                this.$refs.tabComponent.resetDrillDown()
            }
        },
        async fetchStats() {
            try {
                const response = await axios.get('/api/v3/product/stats')
                this.stats = response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
    },
    mounted() {
        // Initialize from URL if present OR from initialTab prop
        const urlParams = new URLSearchParams(window.location.search)
        const tab = urlParams.get('tab')
        const validTabs = ['products', 'categories', 'suppliers', 'brands']

        if (tab && validTabs.includes(tab)) {
            this.activeTab = tab
        } else if (this.initialTab && validTabs.includes(this.initialTab)) {
            this.activeTab = this.initialTab
        }

        this.fetchStats()
        // Remove white background from parent container for V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('product-v3-no-bg')
        }
    },
    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('product-v3-no-bg')
        }
    },
}
</script>

<style scoped lang="scss">
.product-v3-container {
    padding: 0;
}
</style>

<style lang="scss">
#main-container.product-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

#main-container:has(.product-v3-container) {
    background: transparent !important;
}

.dark #main-container.product-v3-no-bg,
.dark #main-container:has(.product-v3-container) {
    background: transparent !important;
}
</style>
